/**
 * useServiceCalculations - Calculates prices for Podcast, Meditaciones, AudioCursos
 */
import { useMemo } from 'react';
import { PODCAST_PACKAGES } from './addonConstants';

export function useServiceCalculations(settings, serviceState) {
  const {
    selectedPodcastPackage, selectedPodcastAddons, podcastQuantity, podcastData,
    podcastPostProduction,
    selectedMeditacionesPackage, selectedMeditacionesAddons, meditacionesQuantity, meditacionesData,
    selectedAudiocursosPackage, selectedAudiocursosAddons, audiocursosQuantity, audiocursosData
  } = serviceState;

  return useMemo(() => {
    // ===== PODCAST =====
    const podcastPrices = {
      'pod_essential': settings.price_pod_essential || 997,
      'pod_platino': settings.price_pod_platino || 3500,
      'pod_premium': settings.price_pod_premium || 5500
    };
    const podcastAddonPrices = {
      'pod_set_basico': settings.price_pod_set_basico || 850,
      'pod_set_virtual': settings.price_pod_set_virtual || 350
    };

    const selectedPodcastPkg = selectedPodcastPackage
      ? { ...PODCAST_PACKAGES.find(p => p.id === selectedPodcastPackage), price: podcastPrices[selectedPodcastPackage] || 0 }
      : null;
    const podcastAddonsSubtotal = (selectedPodcastAddons || []).reduce((sum, id) => sum + (podcastAddonPrices[id] || 0), 0);
    const estimatedRecordingHours = podcastData.episodeDuration
      ? (podcastQuantity * podcastData.episodeDuration * 2.5) / 60
      : 0;
    const postProdMultiplier = podcastPostProduction ? 2 : 1;
    const podcastTotal = selectedPodcastPkg?.price
      ? (estimatedRecordingHours > 0
        ? ((selectedPodcastPkg.price * estimatedRecordingHours) * postProdMultiplier) + podcastAddonsSubtotal
        : ((selectedPodcastPkg.price * podcastQuantity) * postProdMultiplier) + podcastAddonsSubtotal)
      : 0;

    // ===== MEDITACIONES =====
    const medPrices = {
      'med_essential': settings.price_med_essential || 1997,
      'med_platino': settings.price_med_platino || 3997,
      'med_premium': settings.price_med_premium || 6997
    };
    const meditacionesEstimatedHours = meditacionesData.episodeDuration
      ? (meditacionesQuantity * meditacionesData.episodeDuration * 2) / 60
      : 0;
    const meditacionesAddonsTotal = (selectedMeditacionesAddons || []).reduce((sum, id) => {
      const map = { 'med_musica_original': settings.price_med_musica_original || 16997, 'med_efectos_binaurales': settings.price_med_efectos_binaurales || 4997 };
      return sum + (map[id] || 0);
    }, 0);
    const meditacionesTotal = selectedMeditacionesPackage
      ? (meditacionesEstimatedHours > 0
        ? (medPrices[selectedMeditacionesPackage] || 0) * meditacionesEstimatedHours + meditacionesAddonsTotal
        : (medPrices[selectedMeditacionesPackage] || 0) * meditacionesQuantity + meditacionesAddonsTotal)
      : 0;

    // ===== AUDIOCURSOS =====
    const acPrices = {
      'ac_essential': settings.price_ac_essential || 997,
      'ac_platino': settings.price_ac_platino || 3500,
      'ac_premium': settings.price_ac_premium || 5500
    };
    const audiocursosEstimatedHours = audiocursosData.episodeDuration
      ? (audiocursosQuantity * audiocursosData.episodeDuration * 2.5) / 60
      : 0;
    const audiocursosAddonsTotal = (selectedAudiocursosAddons || []).reduce((sum, id) => {
      const map = { 'ac_set_basico': settings.price_ac_set_basico || 5500, 'ac_set_virtual': settings.price_ac_set_virtual || 2500 };
      return sum + (map[id] || 0);
    }, 0);
    const audiocursosTotal = selectedAudiocursosPackage
      ? (audiocursosEstimatedHours > 0
        ? (acPrices[selectedAudiocursosPackage] || 0) * audiocursosEstimatedHours + audiocursosAddonsTotal
        : (acPrices[selectedAudiocursosPackage] || 0) * audiocursosQuantity + audiocursosAddonsTotal)
      : 0;

    return {
      selectedPodcastPkg,
      podcastAddonsSubtotal,
      estimatedRecordingHours,
      podcastTotal,
      meditacionesEstimatedHours,
      meditacionesAddonsTotal,
      meditacionesTotal,
      audiocursosEstimatedHours,
      audiocursosAddonsTotal,
      audiocursosTotal
    };
  }, [
    settings,
    selectedPodcastPackage, selectedPodcastAddons, podcastQuantity, podcastData, podcastPostProduction,
    selectedMeditacionesPackage, selectedMeditacionesAddons, meditacionesQuantity, meditacionesData,
    selectedAudiocursosPackage, selectedAudiocursosAddons, audiocursosQuantity, audiocursosData
  ]);
}
